/**
 * Profile backend capabilities (BE-147 / BE-182, shipped in release 2026-11).
 * Off → the UI runs its mock in dev and hides the action in a production build,
 * so a deployed stand never claims "link sent" when nothing was sent.
 * On → the real call:
 *   VITE_PROFILE_API="change_email,change_password"   (or "all")
 * Dev / preview override: localStorage.setItem('fair:profile-api', 'all') → reload.
 */
import { resolveCapabilities } from '@/shared/capabilities'

export const PROFILE_API_CAPABILITIES = [
  /** `POST /auth/change-email` — a confirmation link to the new address */
  'change_email',
  /** `POST /auth/change-password` — current + new password */
  'change_password',
  /** `email_verified` in profile-data + `POST /auth/resend-verification` */
  'email_verification',
  /** `identity_provider` in `/user/profile` and profile-data — password vs Google / Yandex / VK */
  'auth_provider',
  /** Decision 18 — `GET /consents/current` + `POST /consents/accept`: banner and sign-up checkbox */
  'consents',
] as const

export type ProfileApiCapability = (typeof PROFILE_API_CAPABILITIES)[number]

export const PROFILE_API_STORAGE_KEY = 'fair:profile-api'

const enabled = resolveCapabilities(
  import.meta.env.VITE_PROFILE_API,
  PROFILE_API_STORAGE_KEY,
  PROFILE_API_CAPABILITIES,
)

export function hasProfileApi(capability: ProfileApiCapability): boolean {
  return enabled.has(capability)
}

/** The action is offered: real API, or its mock on a dev server. */
export function isProfileActionAvailable(capability: ProfileApiCapability): boolean {
  return hasProfileApi(capability) || import.meta.env.DEV
}
